/**
 * MUS-625 - what the tutorial does when the app or tab goes to the background
 * and comes back.
 *
 * Going away always pauses: the pause overlay goes up and the exercise scene is
 * told to pause. Coming back depends on what was on screen:
 *   - an exercise is showing: it STAYS PAUSED under its overlay until the
 *     player taps it or presses Space / Enter. It used to be resumed the
 *     instant the page was visible again, with the player not yet at the keys:
 *     the rest of the phrase went by as misses and it ended in "Try again!".
 *   - only the video is showing: in a browser it carries on by itself, as it
 *     always has. In the Android app it stays paused under the overlay and one
 *     tap plays it (see `holdsVideoOnReturn`).
 *
 * A pause the player made before leaving is never undone here.
 *
 * The same pair of calls serves the page visibility event and the native app
 * state event. Both can fire for one trip to the background, in either order,
 * so each call is safe to repeat.
 *
 * The side effects are injected so this can be unit tested, against the real
 * exercise scenes, without rendering the tutorial template.
 */

export interface TutorialBackgroundPauseDeps {
  /** True while the pause overlay is already up. */
  isPauseOverlayShown: () => boolean;
  /** True while an exercise is on screen, over the video. */
  isExerciseShowing: () => boolean;
  showPauseOverlay: () => void;
  hidePauseOverlay: () => void;
  pauseExercise: () => void;
  resumeExercise: () => void;
  /**
   * True in the Android app. Each time the app comes back to the foreground
   * its native shell takes the audio focus, and the WebView stops the video
   * when it loses it. Starting the video again by itself on the way back often
   * lost that race: the overlay came down over a still frame and nothing was
   * left to tap. So every return holds the video paused under the overlay, and
   * the player's tap plays it. That includes a return with no trip reported
   * before it: the app switcher pauses the app without hiding the page or
   * reporting that it left.
   */
  holdsVideoOnReturn?: boolean;
  /** Pause the video element itself, whatever the player component thinks. */
  pauseVideo?: () => void;
  /**
   * True once the tutorial's end screen (its Proceed button) is up. There is
   * no video left to hold, so a return takes the overlay down as in a browser:
   * held under it, Proceed could not be tapped, and a tap on the overlay
   * played the finished video again from the start.
   */
  isProceedShowing?: () => boolean;
  /** The clock, in milliseconds. */
  now?: () => number;
}

/**
 * MUS-651: one trip to the background is reported twice on the way back in the
 * Android app (the app state event and the page visibility event, 0.05-0.43 s
 * apart). A tap on the overlay that came between the two was undone by the
 * second: the overlay came back and the player had to tap again. A return
 * reported this soon after the player's tap is that second report.
 */
export const RETURN_REPORT_AFTER_TAP_MS = 1000;

export interface TutorialBackgroundPause {
  /** The page was hidden, or the app left the foreground. */
  onBackground: () => void;
  /** The page is visible again, or the app is back in the foreground. */
  onForeground: () => void;
  /** The player took the pause overlay down (a tap on it). */
  onPlayerResumed: () => void;
}

export function makeTutorialBackgroundPause(
  deps: TutorialBackgroundPauseDeps,
): TutorialBackgroundPause {
  // True while the overlay is up because of the background, not the player.
  let pausedByBackground = false;
  // When the player last took the overlay down, since the last trip away.
  let playerResumedAt: number | undefined;
  const now = deps.now ?? (() => Date.now());
  const isRightAfterPlayerResumed = () =>
    playerResumedAt !== undefined && now() - playerResumedAt < RETURN_REPORT_AFTER_TAP_MS;

  return {
    onBackground: () => {
      playerResumedAt = undefined;
      // MUS-651: in the Android app, on the end screen, a trip away or a call
      // taking the audio stops the video without the overlay, which would lie
      // over Proceed (and a tap on it would play the finished video again).
      if (deps.holdsVideoOnReturn && deps.isProceedShowing?.()) {
        deps.pauseExercise();
        deps.pauseVideo?.();
        return;
      }
      if (!deps.isPauseOverlayShown()) pausedByBackground = true;
      deps.showPauseOverlay();
      deps.pauseExercise();
      deps.pauseVideo?.();
    },
    onForeground: () => {
      if (deps.holdsVideoOnReturn && !deps.isProceedShowing?.()) {
        // Every report of a return, so a video the WebView started by itself
        // in between is paused again; not the second report of a return the
        // player has already tapped through.
        if (isRightAfterPlayerResumed()) return;
        deps.showPauseOverlay();
        deps.pauseExercise();
        deps.pauseVideo?.();
        return;
      }
      if (!pausedByBackground) return;
      pausedByBackground = false;
      if (deps.isExerciseShowing()) return;
      deps.hidePauseOverlay();
      deps.resumeExercise();
    },
    onPlayerResumed: () => {
      playerResumedAt = now();
    },
  };
}

/**
 * MUS-626 - closing the "connect your keyboard" prompt takes the pause overlay
 * down with it. That is only right when nothing is held paused underneath: an
 * exercise the scene still holds paused (the app went to the background while
 * it ran) keeps its overlay, so the player's tap resumes it. Without this the
 * exercise is left frozen with nothing on screen to say why.
 */
export function keepsPauseOverlayAfterMidiPrompt(state: {
  exerciseShowing: boolean;
  exercisePaused: boolean;
}): boolean {
  return state.exerciseShowing && state.exercisePaused;
}
